import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = ({ data, onFilterChange }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    operation: '',
    unit: '',
    department: '',
    period: '',
    group_by_title: false,
    group_by_mode: '', // 'operation', 'unit', 'both'
    show_targets: false,
    show_previous_year: false,
  });

  const [searchName, setSearchName] = useState(''); // Filtro local por nome
  const [collapsedSections, setCollapsedSections] = useState({});
  const [showGroupByModal, setShowGroupByModal] = useState(false);

  // Cores para as linhas do gráfico quando agrupado por título
  const lineColors = [
    '#10b981', // Verde
    '#3b82f6', // Azul
    '#f59e0b', // Amarelo
    '#ef4444', // Vermelho
    '#8b5cf6', // Roxo
    '#ec4899', // Rosa
    '#14b8a6', // Teal
    '#f97316', // Laranja
  ];

  // Função para normalizar texto (remove acentos e converte para minúsculas)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .trim();
  };

  // Agrupar dados por Operação e Unidade OU por Título (hook deve vir antes de qualquer return)
  const groupedData = useMemo(() => {
    if (!data?.dashboard_data || data.dashboard_data.length === 0) return {};

    // Filtrar por nome se houver busca
    let filteredData = data.dashboard_data;
    if (searchName.trim()) {
      const searchNormalized = normalizeText(searchName);
      filteredData = data.dashboard_data.filter(goal => {
        const titleNormalized = normalizeText(goal.display_title || goal.title || '');
        return titleNormalized.includes(searchNormalized);
      });
    }

    // Se agrupar, criar estrutura diferente baseada no modo
    if (filters.group_by_title && filters.group_by_mode) {
      const groups = {};
      const mode = filters.group_by_mode; // 'operation', 'unit', 'both'
      
      // Mapa para guardar o primeiro título original encontrado para cada título normalizado
      const originalTitles = {};
      
      filteredData.forEach(goal => {
        const displayTitle = goal.display_title || goal.title || 'Sem Título';
        const titleNormalized = normalizeText(displayTitle);
        
        // Guardar o primeiro título original para exibição
        if (!originalTitles[titleNormalized]) {
          originalTitles[titleNormalized] = displayTitle;
        }
        
        // Processar cada graph_point individualmente para capturar operação/unidade de cada um
        if (goal.graph_points && goal.graph_points.length > 0) {
          goal.graph_points.forEach(point => {
            // Usar operação/unidade do ponto se disponível, senão do goal
            const operationName = point.operation_name || goal.operation?.name || 'Sem Operação';
            const operationNormalized = normalizeText(operationName);
            const unitName = point.unit_name || goal.unit_info?.name || goal.unit || 'Sem Unidade';
            const unitNormalized = normalizeText(unitName);
            
            // Determinar a chave do card/grupo e a chave da linha baseado no modo
            let cardKey, lineKey, cardLabel;
            
            if (mode === 'operation') {
              // Card por título + operação, linhas por unidade
              cardKey = `${titleNormalized}|||${operationNormalized}`;
              cardLabel = `${originalTitles[titleNormalized]} (${operationName})`;
              lineKey = unitName;
            } else if (mode === 'unit') {
              // Card por título + unidade, linhas por operação
              cardKey = `${titleNormalized}|||${unitNormalized}`;
              cardLabel = `${originalTitles[titleNormalized]} (${unitName})`;
              lineKey = operationName;
            } else { // 'both'
              // Card por título, linhas por operação + unidade
              cardKey = titleNormalized;
              cardLabel = originalTitles[titleNormalized];
              lineKey = `${operationName} - ${unitName}`;
            }
            
            if (!groups[cardKey]) {
              groups[cardKey] = {
                title: cardLabel,
                operation: mode === 'operation' ? operationName : 'Agrupado',
                unit: mode === 'unit' ? unitName : 'Agrupado',
                goals: [],
                byLineKey: {} // Dados separados pela chave da linha
              };
            }
            
            // Agrupar pela chave da linha
            if (!groups[cardKey].byLineKey[lineKey]) {
              groups[cardKey].byLineKey[lineKey] = {
                label: lineKey,
                operationName,
                unitName,
                points: []
              };
            }
            
            // Verificar se já existe um ponto para esta data na mesma linha
            const existingPoint = groups[cardKey].byLineKey[lineKey].points.find(
              p => p.date === point.date
            );
            
            if (existingPoint) {
              // Acumular valores
              existingPoint.value = (parseFloat(existingPoint.value) || 0) + (parseFloat(point.value) || 0);
              existingPoint.target = (parseFloat(existingPoint.target) || 0) + (parseFloat(point.target) || 0);
              existingPoint.count = (existingPoint.count || 1) + 1;
            } else {
              groups[cardKey].byLineKey[lineKey].points.push({
                ...point,
                value: parseFloat(point.value) || 0,
                target: parseFloat(point.target) || 0,
                count: 1
              });
            }
          });
        }
        
        // Adicionar goal ao grupo baseado no título normalizado (para contagem)
        const goalCardKey = mode === 'both' ? titleNormalized : 
          mode === 'operation' ? `${titleNormalized}|||${normalizeText(goal.operation?.name || '')}` :
          `${titleNormalized}|||${normalizeText(goal.unit_info?.name || goal.unit || '')}`;
        
        if (groups[goalCardKey] && !groups[goalCardKey].goals.includes(goal)) {
          groups[goalCardKey].goals.push(goal);
        }
      });

      // Ordenar pontos por data em cada linha
      Object.values(groups).forEach(group => {
        Object.values(group.byLineKey).forEach(lineData => {
          lineData.points.sort((a, b) => new Date(a.date) - new Date(b.date));
        });
      });

      // Ordenar cards por título
      const sortedKeys = Object.keys(groups).sort();
      const sortedGroups = {};
      sortedKeys.forEach(key => {
        sortedGroups[key] = groups[key];
      });

      return sortedGroups;
    }

    // Agrupamento padrão por Operação-Unidade
    const groups = {};
    
    filteredData.forEach(goal => {
      // O backend retorna operation e unit_info como objetos
      const operationName = goal.operation?.name || 'Sem Operação';
      const unitName = goal.unit_info?.name || goal.unit || 'Sem Unidade';
      const groupKey = `${operationName}|||${unitName}`;
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          operation: operationName,
          unit: unitName,
          goals: []
        };
      }
      groups[groupKey].goals.push(goal);
    });

    // Ordenar grupos por operação e unidade
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const [opA, unitA] = a.split('|||');
      const [opB, unitB] = b.split('|||');
      if (opA !== opB) return opA.localeCompare(opB);
      return unitA.localeCompare(unitB);
    });

    const sortedGroups = {};
    sortedKeys.forEach(key => {
      sortedGroups[key] = groups[key];
      // Ordenar metas dentro de cada grupo por título
      sortedGroups[key].goals.sort((a, b) => 
        (a.display_title || a.title).localeCompare(b.display_title || b.title)
      );
    });

    return sortedGroups;
  }, [data?.dashboard_data, searchName, filters.group_by_title, filters.group_by_mode]);

  // Filtros que são apenas de visualização local (não vão para o backend)
  const localOnlyFilters = ['group_by_title', 'group_by_mode', 'show_targets', 'show_previous_year'];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Só chama o backend se NÃO for um filtro local
    if (onFilterChange && !localOnlyFilters.includes(key)) {
      // Remover filtros locais antes de enviar ao backend
      const backendFilters = { ...newFilters };
      localOnlyFilters.forEach(f => delete backendFilters[f]);
      onFilterChange(backendFilters);
    }
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  if (!data) {
    return <div className="goals-list-page"><div className="goals-loading">Carregando...</div></div>;
  }

  // Formatar data para exibição no gráfico
  // Usa parsing manual para evitar problemas de fuso horário
  const formatChartDate = (dateStr) => {
    if (!dateStr) return '';
    
    // Parse manual para evitar problemas de timezone
    // dateStr pode ser "YYYY-MM-DD" ou "YYYY-MM-DDTHH:mm:ss..."
    const parts = String(dateStr).split('T')[0].split('-');
    if (parts.length !== 3) return dateStr;
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexed
    const day = parseInt(parts[2], 10);
    
    // Criar data local (não UTC)
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return dateStr;
    
    return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
  };

  // Formatar número com milhares e decimais automáticos (pt-BR)
  // Remove decimais se forem 00
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    const value = parseFloat(num);
    const hasDecimals = value % 1 !== 0;
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2
    });
  };

  // Formatar número para stats (sempre inteligente)
  const formatStatValue = (num) => {
    if (!num && num !== 0) return '0';
    const value = parseFloat(num);
    // Se o número é inteiro ou decimais são 00, não mostrar decimais
    if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 0.005) {
      return Math.round(value).toLocaleString('pt-BR');
    }
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Renderizar card agrupado por título (múltiplas linhas)
  const renderGroupedByTitleCard = (groupData, titleKey) => {
    const lineKeys = Object.keys(groupData.byLineKey || {});
    if (lineKeys.length === 0) return null;

    // Coletar todos os períodos únicos
    const allDates = new Set();
    lineKeys.forEach(lineKey => {
      groupData.byLineKey[lineKey].points.forEach(p => {
        if (p.date) allDates.add(p.date);
      });
    });

    // Ordenar datas
    const sortedDates = Array.from(allDates).sort();

    // Criar dados para o gráfico com uma coluna para cada linha
    const chartData = sortedDates.map(date => {
      const dataPoint = { 
        date, 
        label: formatChartDate(date) 
      };
      
      lineKeys.forEach((lineKey, idx) => {
        const points = groupData.byLineKey[lineKey].points;
        const point = points.find(p => p.date === date);
        dataPoint[`value_${idx}`] = point ? parseFloat(point.value) || 0 : null;
        dataPoint[`target_${idx}`] = point ? parseFloat(point.target) || 0 : null;
      });
      
      return dataPoint;
    });

    // Calcular domínio do eixo Y
    const allValues = chartData.flatMap(d => 
      lineKeys.flatMap((_, idx) => [d[`value_${idx}`], d[`target_${idx}`]]).filter(v => v != null)
    );
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 100;
    const minValue = allValues.length > 0 ? Math.min(...allValues.filter(v => v > 0)) : 0;
    const range = maxValue - minValue || maxValue || 100;
    const yMin = Math.max(0, minValue - range * 0.3);
    const yMax = maxValue + range * 0.3;

    // Calcular estatísticas agregadas
    const allGoalValues = groupData.goals.flatMap(g => 
      g.graph_points?.map(p => parseFloat(p.value) || 0) || []
    );
    const avgValue = allGoalValues.length > 0 
      ? allGoalValues.reduce((a, b) => a + b, 0) / allGoalValues.length 
      : 0;
    const bestValue = allGoalValues.length > 0 ? Math.max(...allGoalValues) : 0;

    // Label do modo de agrupamento (as linhas mostram o oposto do agrupamento)
    const modeLabel = filters.group_by_mode === 'operation' ? 'unidade' 
      : filters.group_by_mode === 'unit' ? 'operação' : 'op/unid';

    return (
      <div 
        key={titleKey} 
        className="dashboard-card dashboard-card-grouped"
        style={{ cursor: 'default' }}
      >
        <div className="dashboard-card-header">
          <h3 className="dashboard-card-title">{groupData.title}</h3>
          <span className="badge bg-info" style={{ fontSize: '0.7rem' }}>
            {lineKeys.length} {modeLabel}{lineKeys.length > 1 ? 's' : ''}
          </span>
        </div>
        
        <div className="dashboard-card-stats">
          <div className="dashboard-stat">
            <span className="dashboard-stat-value">{formatStatValue(avgValue)}</span>
            <span className="dashboard-stat-label">Média Geral</span>
          </div>
          <div className="dashboard-stat">
            <span className="dashboard-stat-value">{formatStatValue(bestValue)}</span>
            <span className="dashboard-stat-label">Melhor</span>
          </div>
          <div className="dashboard-stat">
            <span className="dashboard-stat-value">{groupData.goals.length}</span>
            <span className="dashboard-stat-label">Metas</span>
          </div>
        </div>

        {/* Legenda */}
        <div className="dashboard-chart-legend">
          {lineKeys.map((lineKey, idx) => (
            <div key={lineKey} className="dashboard-legend-item">
              <span 
                className="dashboard-legend-color" 
                style={{ backgroundColor: lineColors[idx % lineColors.length] }}
              ></span>
              <span className="dashboard-legend-text">{lineKey}</span>
            </div>
          ))}
        </div>
        
        {chartData.length > 0 && (
          <div className="dashboard-card-chart" style={{ height: '180px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 8, fill: 'rgba(255,255,255,0.6)' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  domain={[yMin, yMax]}
                  tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.6)' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  tickFormatter={(value) => formatNumber(value).split(',')[0]}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1a2e', 
                    border: '1px solid rgba(197,165,114,0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '11px'
                  }}
                  formatter={(value, name) => {
                    if (value == null) return ['-', name];
                    const parts = name.split('_');
                    const type = parts[0]; // 'value' ou 'target'
                    const idx = parseInt(parts[1]);
                    const label = lineKeys[idx] || '';
                    const suffix = type === 'target' ? ' (Meta)' : '';
                    return [formatNumber(value), label + suffix];
                  }}
                  labelFormatter={(label) => `Período: ${label}`}
                />
                {/* Linhas de Meta (tracejadas) - apenas se show_targets ativo */}
                {filters.show_targets && lineKeys.map((lineKey, idx) => (
                  <Line 
                    key={`target_${lineKey}`}
                    type="monotone" 
                    dataKey={`target_${idx}`}
                    stroke={lineColors[idx % lineColors.length]}
                    name={`target_${idx}`}
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    strokeOpacity={0.5}
                    dot={false}
                    connectNulls
                  />
                ))}
                {/* Linhas de Realizado */}
                {lineKeys.map((lineKey, idx) => (
                  <Line 
                    key={lineKey}
                    type="monotone" 
                    dataKey={`value_${idx}`}
                    stroke={lineColors[idx % lineColors.length]}
                    name={`value_${idx}`}
                    strokeWidth={2}
                    dot={{ r: 2, fill: lineColors[idx % lineColors.length] }}
                    activeDot={{ r: 4 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  const renderGoalCard = (goal, index) => {
    // Ordenar pontos do gráfico por data (mais antiga para mais recente)
    const sortedPoints = goal.graph_points 
      ? [...goal.graph_points].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB;
        }).map(point => ({
          ...point,
          label: formatChartDate(point.date)
        }))
      : [];

    // Calcular domínio do eixo Y com margem de 30%
    const allValues = sortedPoints.flatMap(p => [
      parseFloat(p.value) || 0, 
      parseFloat(p.target) || 0
    ]);
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 100;
    const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
    const range = maxValue - minValue || maxValue || 100;
    const yMin = Math.max(0, minValue - range * 0.3);
    const yMax = maxValue + range * 0.3;

    return (
      <div 
        key={goal.id || index} 
        className="dashboard-card"
        onClick={() => navigate(`/goals/${goal.latest_goal_id || goal.id}?detail=true`)}
        style={{ cursor: 'pointer' }}
        title="Clique para ver detalhes"
      >
        <div className="dashboard-card-header">
          <h3 className="dashboard-card-title">{goal.display_title || goal.title}</h3>
          <span className="dashboard-card-progress" style={{
            color: goal.avg_progress >= 100 ? '#10b981' : goal.avg_progress >= 70 ? '#c5a572' : '#ef4444'
          }}>
            {goal.avg_progress?.toFixed(0) || 0}%
          </span>
        </div>
        
        <div className="dashboard-card-stats">
          <div className="dashboard-stat">
            <span className="dashboard-stat-value">{formatStatValue(goal.avg_value)}</span>
            <span className="dashboard-stat-label">Média</span>
          </div>
          <div className="dashboard-stat">
            <span className="dashboard-stat-value">{formatStatValue(goal.best_value)}</span>
            <span className="dashboard-stat-label">Melhor</span>
          </div>
          <div className="dashboard-stat">
            <span className="dashboard-stat-value">{goal.unit || '-'}</span>
            <span className="dashboard-stat-label">Unidade</span>
          </div>
        </div>
        
        {sortedPoints.length > 0 && (
          <div className="dashboard-card-chart">
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={sortedPoints} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 8, fill: 'rgba(255,255,255,0.6)' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  domain={[yMin, yMax]}
                  tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.6)' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  tickFormatter={(value) => formatNumber(value).split(',')[0]}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1a2e', 
                    border: '1px solid rgba(197,165,114,0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '12px'
                  }}
                  formatter={(value, name) => [formatNumber(value), name === 'value' ? 'Realizado' : 'Meta']}
                  labelFormatter={(label) => `Período: ${label}`}
                />
                {filters.show_targets && (
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    stroke="rgba(197, 165, 114, 0.6)" 
                    name="Meta"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 2, fill: 'rgba(197, 165, 114, 0.6)' }}
                  />
                )}
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#10b981" 
                  name="Realizado"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#10b981' }}
                  activeDot={{ r: 5, fill: '#10b981' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  // Handler para seleção do modo de agrupamento (apenas visual, não chama backend)
  const handleGroupByModeSelect = (mode) => {
    setFilters(prev => ({ 
      ...prev, 
      group_by_mode: mode, 
      group_by_title: true 
    }));
    setShowGroupByModal(false);
  };

  return (
    <div className="dashboard goals-list-page">
      {/* Modal de seleção de modo de agrupamento */}
      {showGroupByModal && (
        <div className="dashboard-modal-overlay" onClick={() => setShowGroupByModal(false)}>
          <div className="dashboard-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h3>Como deseja agrupar?</h3>
              <button 
                className="dashboard-modal-close" 
                onClick={() => setShowGroupByModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="dashboard-modal-body">
              <p>Selecione como as linhas do gráfico devem ser separadas:</p>
              <div className="dashboard-modal-options">
                <button 
                  className="dashboard-modal-option"
                  onClick={() => handleGroupByModeSelect('operation')}
                >
                  <i className="fas fa-building"></i>
                  <span className="option-title">Agrupar por Operação</span>
                  <span className="option-desc">Card por operação, uma linha para cada unidade</span>
                </button>
                <button 
                  className="dashboard-modal-option"
                  onClick={() => handleGroupByModeSelect('unit')}
                >
                  <i className="fas fa-map-marker-alt"></i>
                  <span className="option-title">Agrupar por Unidade</span>
                  <span className="option-desc">Card por unidade, uma linha para cada operação</span>
                </button>
                <button 
                  className="dashboard-modal-option"
                  onClick={() => handleGroupByModeSelect('both')}
                >
                  <i className="fas fa-layer-group"></i>
                  <span className="option-title">Agrupar por Título</span>
                  <span className="option-desc">Card por título, uma linha para cada op+unidade</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Dashboard de Metas</h1>
        <button 
          className="btn btn-sm"
          onClick={() => navigate('/dashboard/custom')}
          title="Dashboard Personalizado"
          style={{
            backgroundColor: 'transparent',
            border: '2px solid #d4a84b',
            color: '#d4a84b',
            fontWeight: 600,
            padding: '0.4rem 1rem',
            whiteSpace: 'nowrap',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#d4a84b';
            e.target.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = '#d4a84b';
          }}
        >
          <i className="fas fa-th-large me-1"></i>
          Personalizar
        </button>
      </header>

      <div className="dashboard-filters">
        <div className="dashboard-search-wrapper">
          <i className="fas fa-search dashboard-search-icon"></i>
          <input 
            type="text"
            className="dashboard-search-input"
            placeholder="Buscar por nome..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
          {searchName && (
            <button 
              className="dashboard-search-clear"
              onClick={() => setSearchName('')}
              title="Limpar busca"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <select 
          value={filters.operation} 
          onChange={(e) => handleFilterChange('operation', e.target.value)}
        >
          <option value="">Todas as Operações</option>
          {data.operations?.map(op => (
            <option key={op.id} value={op.code}>{op.name}</option>
          ))}
        </select>

        <select 
          value={filters.unit} 
          onChange={(e) => handleFilterChange('unit', e.target.value)}
        >
          <option value="">Todas as Unidades</option>
          {data.units?.map(unit => (
            <option key={unit.id} value={unit.code}>{unit.name}</option>
          ))}
        </select>

        <select 
          value={filters.period} 
          onChange={(e) => handleFilterChange('period', e.target.value)}
        >
          <option value="">Todos os Períodos</option>
          <option value="current_year">Ano Atual</option>
          <option value="previous_year">Ano Anterior</option>
          <option value="last_12_months">Últimos 12 Meses</option>
          {data.available_years?.map(year => (
            <option key={year} value={`year_${year}`}>{year}</option>
          ))}
        </select>

        <div className="dashboard-group-by-wrapper">
          <label className="dashboard-checkbox-label">
            <input 
              type="checkbox" 
              checked={filters.group_by_title}
              onChange={(e) => {
                if (e.target.checked) {
                  setShowGroupByModal(true);
                } else {
                  // Apenas visual, não chama backend
                  setFilters(prev => ({ 
                    ...prev, 
                    group_by_title: false, 
                    group_by_mode: '' 
                  }));
                }
              }}
            />
            <span>Agrupar por Título</span>
          </label>
          
          {filters.group_by_title && filters.group_by_mode && (
            <select 
              className="dashboard-group-mode-select"
              value={filters.group_by_mode}
              onChange={(e) => handleFilterChange('group_by_mode', e.target.value)}
            >
              <option value="operation">Por Operação (linhas = unidades)</option>
              <option value="unit">Por Unidade (linhas = operações)</option>
              <option value="both">Por Título (linhas = op+unidade)</option>
            </select>
          )}
        </div>

        <label className="dashboard-checkbox-label">
          <input 
            type="checkbox" 
            checked={filters.show_targets}
            onChange={(e) => handleFilterChange('show_targets', e.target.checked)}
          />
          <span>Mostrar Metas</span>
        </label>
      </div>

      <div className="dashboard-content">
        {Object.keys(groupedData).length === 0 ? (
          <div className="no-data">
            <p>Nenhuma meta encontrada com os filtros selecionados.</p>
          </div>
        ) : (filters.group_by_title && filters.group_by_mode) ? (
          /* Modo agrupado por título - cada card mostra múltiplas linhas */
          <div className="dashboard-grouped-by-title">
            <div className="dashboard-grid">
              {Object.entries(groupedData).map(([titleKey, groupData]) => 
                renderGroupedByTitleCard(groupData, titleKey)
              )}
            </div>
          </div>
        ) : (
          /* Modo padrão - agrupado por operação-unidade */
          Object.entries(groupedData).map(([key, group]) => {
            const isCollapsed = collapsedSections[key];
            const sectionId = key.replace(/[^a-zA-Z0-9]/g, '-');
            
            return (
              <div key={key} className="dashboard-section">
                <div 
                  className="dashboard-section-header"
                  onClick={() => toggleSection(key)}
                >
                  <div className="dashboard-section-title">
                    <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'}`}></i>
                    <span className="dashboard-section-operation">{group.operation}</span>
                    <span className="dashboard-section-separator">›</span>
                    <span className="dashboard-section-unit">{group.unit}</span>
                  </div>
                  <span className="dashboard-section-count">
                    {group.goals.length} {group.goals.length === 1 ? 'meta' : 'metas'}
                  </span>
                </div>
                
                {!isCollapsed && (
                  <div className="dashboard-grid" id={sectionId}>
                    {group.goals.map((goal, index) => renderGoalCard(goal, index))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Dashboard;
